// API utility for handling base URLs in development vs production
const BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5000' 
  : '';

/**
 * Centralized fetch function that handles base URL automatically
 * @param {string} endpoint - The API endpoint (e.g., '/api/auth/login')
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch response
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = `${BASE_URL}${endpoint}`;
  return fetch(url, options);
};

/**
 * Helper function for GET requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiGet = async (endpoint, options = {}) => {
  return apiFetch(endpoint, {
    method: 'GET',
    credentials: 'include',
    ...options
  });
};

/**
 * Helper function for POST requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - Data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiPost = async (endpoint, data = null, options = {}) => {
  const fetchOptions = {
    method: 'POST',
    credentials: 'include',
    ...options
  };

  if (data) {
    if (data instanceof FormData) {
      fetchOptions.body = data;
    } else {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };
      fetchOptions.body = JSON.stringify(data);
    }
  }

  return apiFetch(endpoint, fetchOptions);
};

/**
 * Helper function for PUT requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - Data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiPut = async (endpoint, data = null, options = {}) => {
  const fetchOptions = {
    method: 'PUT',
    credentials: 'include',
    ...options
  };

  if (data) {
    if (data instanceof FormData) {
      fetchOptions.body = data;
    } else {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };
      fetchOptions.body = JSON.stringify(data);
    }
  }

  return apiFetch(endpoint, fetchOptions);
};

/**
 * Helper function for PATCH requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} data - Data to send
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiPatch = async (endpoint, data = null, options = {}) => {
  const fetchOptions = {
    method: 'PATCH',
    credentials: 'include',
    ...options
  };

  if (data) {
    if (data instanceof FormData) {
      fetchOptions.body = data;
    } else {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };
      fetchOptions.body = JSON.stringify(data);
    }
  }

  return apiFetch(endpoint, fetchOptions);
};

/**
 * Helper function for DELETE requests
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise} - Fetch response
 */
export const apiDelete = async (endpoint, options = {}) => {
  return apiFetch(endpoint, {
    method: 'DELETE',
    credentials: 'include',
    ...options
  });
};

/**
 * Helper function to get proper image URL for development vs production
 * @param {string} imagePath - The image path (e.g., 'filename.jpg' or '/uploads/filename.jpg')
 * @returns {string} - The complete image URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  
  // If it starts with /uploads/, use the base URL
  if (imagePath.startsWith('/uploads/')) {
    return `${BASE_URL}${imagePath}`;
  }
  
  // If it's just a filename, assume it's in uploads
  return `${BASE_URL}/uploads/${imagePath}`;
};

// Export BASE_URL for direct use if needed
export { BASE_URL }; 